/**
 * Which pose source an attempt uses — the whole decision, as one pure function.
 *
 * Pure on purpose. This is the piece of server-side inference with the highest
 * cost of being wrong in the quiet direction, so it has to be exhaustively
 * testable without a network, a worker, a camera or a Firestore read.
 *
 * ── The rule that is easiest to get wrong ────────────────────────────────
 *
 * **Do not key this on "is this a cheap phone".** That was the first draft, and
 * production data says it misses the group that matters most: BUG-0004's
 * population — the one server-side inference recovers *outright* rather than
 * partially — is **62.5% iPhone on recent iOS**, is not slow, and fails on WebGL
 * context loss. And because `PoseDetectionManager.initialize()` is the thing
 * that failed for them, the capability classifier never runs, so they carry
 * `deviceClass: "unknown"`. A class-keyed router routes none of them.
 *
 * So the triggers are, in order: **it failed**, **it is failing right now**, and
 * only then **a benchmark thinks it will**.
 *
 * ── The rule that protects everyone else ─────────────────────────────────
 *
 * A device that is working is never routed. Measured 2026-09-09 on a `standard`
 * iPhone: 12.8 Hz on-device with zero pose-lost time, against 0.6 Hz and 94%
 * pose-lost through the server, with 9 of 35 requests timing out. The server is
 * a rescue, and rescuing something that is not drowning drowns it.
 *
 * Plan: docs/superpowers/plans/2026-09-09-server-side-model-inference-plan.md §3.1
 */

import type { DeviceClass } from "./alignmentTuning";

/** Result of the one-shot reachability probe taken during scan setup. */
export type ProbeState = "pending" | "ok" | "failed";

export type PoseSource = "device" | "server";

export interface PoseSourceConfig {
  /** Master kill switch. Outranks every trigger below. */
  enabled: boolean;
  /** Boot classes eligible for the head start. */
  deviceClasses: DeviceClass[];
  /** Whether a terminal model-init failure routes to the server. */
  onModelFailure: boolean;
  /** Start on the server when the startup benchmark says `fail`. */
  onBenchmarkFail: boolean;
  /** Switch when the phone's detector breaks mid-scan. */
  onDetectorBroken: boolean;
  /** Switch when the phone goes blind with the person clearly in frame. */
  onBlindWhileVisible: boolean;
  /** Switch when the running model cannot see one gated joint (liveHealth.ts). */
  onJointUnseen: boolean;
  /** After a live switch, start later attempts this page load on the server. */
  rememberLiveSwitch: boolean;
}

export interface PoseSourceInputs {
  /** `PoseDetectionManager` emitted `error` — every init attempt is spent. */
  modelsFailed: boolean;
  /** Boot-time verdict. `unknown` means unmeasured, never "slow". */
  deviceClass: DeviceClass;
  /**
   * The phone's own detector — every frame it completed, person found or not —
   * has fallen below the keeping-up threshold this attempt (liveHealth.ts).
   */
  liveDegraded: boolean;
  probe: ProbeState;
  online: boolean;
  config: PoseSourceConfig;
  /** The startup benchmark's accuracy verdict. `unknown` never routes. */
  benchmarkVerdict: "pass" | "fail" | "unknown";
  /** Live: the phone's detector broke this attempt (liveHealth.ts). */
  detectorBroken: boolean;
  /** Live: framed, but every gated joint collapsed for 3 s (liveHealth.ts). */
  blindWhileVisible: boolean;
  /**
   * Live: framed, but the PHONE's model has not seen one gated joint for 5 s and
   * 30% of framed time (liveHealth.ts). A cloud miss is the controller's call,
   * not this function's — this one only ever decides where the phone stands.
   */
  jointUnseen: boolean;
  /** A live trigger switched an earlier attempt this page load. */
  sessionDemoted: boolean;
}

export interface PoseSourceDecision {
  source: PoseSource;
  /** Why, for `scan.alignment.summary`. A decision nobody can explain is a decision nobody can roll back. */
  reason:
    | "disabled"
    | "offline"
    | "models-failed"
    | "live-degraded"
    | "device-class"
    | "device-healthy"
    | "probe-failed"
    | "detector-broken"
    | "blind-while-visible"
    | "joint-unseen"
    | "session-demoted"
    | "benchmark-fail";
}

/**
 * The fail-safe. `RemoteConfigService` falls back to this when its Firestore
 * read fails — a config that could not be read must never enable a feature.
 */
export const DEVICE_ONLY_CONFIG: PoseSourceConfig = {
  enabled: false,
  deviceClasses: [],
  onModelFailure: false,
  onBenchmarkFail: false,
  onDetectorBroken: false,
  onBlindWhileVisible: false,
  onJointUnseen: false,
  rememberLiveSwitch: false,
};

export function decidePoseSource(
  inputs: PoseSourceInputs
): PoseSourceDecision {
  const {
    modelsFailed,
    deviceClass,
    liveDegraded,
    probe,
    online,
    config,
    benchmarkVerdict,
    detectorBroken,
    blindWhileVisible,
    jointUnseen,
    sessionDemoted,
  } = inputs;

  // 1. Kill switch. Checked before anything else so that turning the feature
  //    off is unconditional — no trigger, however severe, may outvote it.
  if (!config.enabled) return { source: "device", reason: "disabled" };

  // 2. Offline. Nothing below can be satisfied by a server we cannot reach.
  if (!online) return { source: "device", reason: "offline" };

  // 3. The models never loaded. No probe gate: these users have no on-device
  //    path at all, so a slow server beats a Start button that spins forever.
  if (modelsFailed && config.onModelFailure) {
    return { source: "server", reason: "models-failed" };
  }

  // 4. Live — the phone's detector broke. The same logic as models-failed and
  //    therefore the same place: nothing is left on the phone to fall back to.
  if (detectorBroken && config.onDetectorBroken) {
    return { source: "server", reason: "detector-broken" };
  }

  // 5. Every remaining trigger is an OPTIMISATION over a detector that still
  //    works, so an unreachable server is strictly worse than a slow local one.
  if (probe !== "ok") return { source: "device", reason: "probe-failed" };

  // 6. Live — too slow on this attempt, right now.
  if (liveDegraded) return { source: "server", reason: "live-degraded" };

  // 7. Live — blind while the person is clearly in frame. Inferred rather than
  //    certain, so it sits behind the probe gate with the other optimisations.
  if (blindWhileVisible && config.onBlindWhileVisible) {
    return { source: "server", reason: "blind-while-visible" };
  }

  // 7b. Live — the phone cannot see ONE gated joint while framed: the narrower
  //     reading of 7, so 7 names the row when both hold. This is Auto's answer
  //     to "struggling to get into position", and deliberately not a timer: a
  //     person standing in the wrong place never trips it, because the other
  //     model sees the same arm in the same wrong place (liveHealth.ts).
  if (jointUnseen && config.onJointUnseen) {
    return { source: "server", reason: "joint-unseen" };
  }

  // 8. A live trigger switched an earlier attempt: start where it ended up
  //    rather than failing the same way again.
  if (sessionDemoted && config.rememberLiveSwitch) {
    return { source: "server", reason: "session-demoted" };
  }

  // 9. The startup benchmark: fast or not, this phone's keypoints were wrong.
  if (benchmarkVerdict === "fail" && config.onBenchmarkFail) {
    return { source: "server", reason: "benchmark-fail" };
  }

  // 10. The boot class, as a head start. `indexOf`, not `includes`: target es5.
  if (config.deviceClasses.indexOf(deviceClass) !== -1) {
    return { source: "server", reason: "device-class" };
  }

  return { source: "device", reason: "device-healthy" };
}
